import { inject, Injectable, Injector, signal } from '@angular/core';
import { handleRequestError } from '@softline/application';
import { ShelfLabelPrintRepository } from '../repositories/shelf-label-print.repository';
import { ShelfLabelQueryStateService } from './shelf-label-query-state.service';
import { ShelfLabelDefinition } from '../types';

export interface PrintOutcome {
  succeeded: string[];
  failed: string[];
}

@Injectable()
export class ShelfLabelPrintService {
  private readonly repository = inject(ShelfLabelPrintRepository);
  private readonly queryState = inject(ShelfLabelQueryStateService);
  private readonly injector = inject(Injector);

  readonly isPrinting = signal(false);
  readonly lastFailures = signal<string[]>([]);

  async printSelected(rows: ShelfLabelDefinition[]): Promise<PrintOutcome> {
    this.isPrinting.set(true);
    const storeId = this.queryState.storeId() ?? '';
    const priceLevelId = this.queryState.priceLevelId() ?? '';

    const succeeded: string[] = [];
    const failed: string[] = [];
    let firstFailureReason: unknown;

    // Promise.allSettled/PromiseRejectedResult aren't available at this
    // workspace's TS lib target — settle each print manually instead.
    await Promise.all(
      rows.map(async (row) => {
        try {
          await this.repository.print({ id: row.id, quantity: 1, storeId, priceLevelId });
          succeeded.push(row.id);
        } catch (e) {
          failed.push(row.id);
          if (firstFailureReason === undefined) firstFailureReason = e;
        }
      })
    );

    this.lastFailures.set(failed);
    this.isPrinting.set(false);

    if (failed.length > 0) {
      // Aggregate all rejections into a single handleRequestError call — never
      // one toast per failed print request.
      handleRequestError(firstFailureReason, this.injector, { show: true });
    }

    return { succeeded, failed };
  }
}
